import { Injectable, inject } from '@angular/core';
import {
  CenterOnExtent,
  CoordinateSystem,
  GeoBrowserService,
  InteractionService,
  MapColors,
} from '@next/arable-lib/geo';
import { CoreActions } from '@next/core-lib';
import { Store, select } from '@ngrx/store';
import { EventsKey } from 'ol/events';
import { never } from 'ol/events/condition';
import { extend } from 'ol/extent';
import Feature from 'ol/Feature';
import { GeoJSON } from 'ol/format';

import { Select } from 'ol/interaction';
import { VectorImage } from 'ol/layer';
import { tile } from 'ol/loadingstrategy';

import { unByKey } from 'ol/Observable';
import { Vector } from 'ol/source';
import { Style, Fill, Stroke } from 'ol/style';
import { createXYZ } from 'ol/tilegrid';
import { filter } from 'rxjs/operators';
import { SiteState } from 'src/app/shared/models';

import { Feature as LocalFeature, InteractionType } from '../../models';

import { InteractionShapefileFieldSelected } from '../../store/actions/feature.actions';
import { SetCropFieldsEnabled } from '../../store/actions/map-context.actions';
import { getShapefileFeatures } from '../../store/selectors/map-context.selectors';

@Injectable({
  providedIn: 'root',
})
export class GeoSelectShapefileService implements InteractionService {
  private readonly store = inject<Store<SiteState>>(Store);

  interactionTypes = [InteractionType.copyShapefileData];
  private selectEventsKey: EventsKey;
  readonly source = new Vector({
    format: new GeoJSON(),
    strategy: tile(createXYZ({})),
  });

  readonly style = new Style({
    stroke: new Stroke({
      color: MapColors.shapefileStroke,
      width: 2,
    }),
    fill: new Fill({
      color: MapColors.invisible,
    }),
  });

  readonly layer = new VectorImage({
    source: this.source,
    style: this.style,
    zIndex: 1,
  });

  readonly select = new Select({
    toggleCondition: never,
    multi: true,
    style: new Style({
      stroke: new Stroke({
        color: MapColors.shapefileSelectStroke,
        width: 2,
      }),
      fill: new Fill({
        color: MapColors.shapefileFill,
      }),
      zIndex: 5,
    }),
    layers: (layer) => layer === this.layer,
  });

  init(geoService: GeoBrowserService) {
    this.store
      .pipe(
        select(getShapefileFeatures),
        filter((features) => !!features?.length),
      )
      .subscribe((features) => {
        try {
          for (const feature of features) {
            const geoFeature = new GeoJSON().readFeature(feature, {
              dataProjection: CoordinateSystem.WorldGeodetic,
              featureProjection: CoordinateSystem.PseudoMercator,
            });
            geoFeature.setId(undefined);
            this.source.addFeature(geoFeature);
          }
          const extent = this.source.getExtent();
          extend(extent, extent);
          this.store.dispatch(new CenterOnExtent(extent));
        } catch (error) {
          console.log('Shapefile: Error in "new GeoJSON().readFeatures(feature, {...})"', error);
        }
      });

    geoService.map.addLayer(this.layer);
    geoService.map.addInteraction(this.select);
    this.select.setActive(false);
    this.layer.setMaxResolution(geoService.map.getView().getResolutionForZoom(12));
  }

  destroy(geoService: GeoBrowserService) {
    geoService.map.removeLayer(this.layer);
    geoService.map.removeInteraction(this.select);
  }

  start(geoService: GeoBrowserService) {
    this.selectEventsKey = this.select.on('select', (selectEvent) => {
      this.store.dispatch(CoreActions.setLoading({ loading: true }));

      setTimeout(() => {
        const features = selectEvent.selected.filter((feature) => !!feature);
        this.store.dispatch(CoreActions.setLoading({ loading: false }));
        this.store.dispatch(
          new InteractionShapefileFieldSelected(this.featureToGeoJson(features[0])),
        );
      }, 0);
    });
    this.select.setActive(true);
    this.store.dispatch(new SetCropFieldsEnabled(true));

    if (geoService.view.getZoom() < 15) {
      geoService.view.setZoom(15);
    }
    this.layer.setVisible(true);
  }

  stop(_: GeoBrowserService) {
    unByKey(this.selectEventsKey);
    this.select.setActive(false);
    this.layer.setStyle(this.style);
    this.layer.setVisible(false);
    this.select.getFeatures().clear();
  }

  private featureToGeoJson(feature: Feature): LocalFeature {
    const writer = new GeoJSON();
    // pass the feature as an array
    return JSON.parse(writer.writeFeature(feature)) as LocalFeature;
  }
}
